import { Dispatch, memo, SetStateAction, useEffect, useState } from 'react';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import styled from 'styled-components';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

import Calendar from '@/assets/refund/calendar.png';
import ActionBtn from '@/components/common/ActionBtn';
import { setFilter } from '@/store/modules/fxReceipt';
import { useAppDispatch, useAppSelector } from '@/hooks/useReduxHooks';
import { defaultStaticRanges } from '@/constants/dateRange';

type DateSelectorFilterProps = {
  isRefundDetail?: boolean;
  setPageNumber?: any;
};

function FXReceiptDateSelector({
  isRefundDetail,
  setPageNumber,
}: DateSelectorFilterProps) {
  const { filter } = useAppSelector((state) => state.fxReceipt);

  const dispatch = useAppDispatch();
  const [date, setDate] = useState({
    startDate: filter.startDate,
    endDate: filter.endDate,
  });
  const { startDate, endDate } = date;
  const [isActiveCalendar, setIsActiveCalendar] = useState(false);

  useEffect(() => {
    if (isRefundDetail) {
      // 전역 상태에 이미 날짜가 설정되어 있으면 초기화하지 않음
      const hasExistingDateRange = filter.startDate && filter.endDate;
      if (!hasExistingDateRange) {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        setDate((prev) => ({ ...prev, startDate: startDate }));
        dispatch(
          setFilter({
            startDate,
            endDate,
          }),
        );
      }
    }
  }, []);

  const onChange = (e: any) => {
    const { startDate, endDate } = e.selection;
    setDate({ startDate, endDate });
  };
  const onApply = (e: any) => {
    dispatch(
      setFilter({
        startDate,
        endDate,
      }),
    );
    onCloseCalendar(e);
    setPageNumber && setPageNumber(0);
  };
  const onCancel = (e: any) => {
    setDate({
      startDate: filter.startDate,
      endDate: filter.endDate,
    });
    onCloseCalendar(e);
  };
  const onCloseCalendar = (e: any) => {
    if (e.target === e.currentTarget) {
      setIsActiveCalendar(false);
    }
  };

  return (
    <>
      <DateSelector onClick={() => setIsActiveCalendar(true)}>
        <Icon src={Calendar} />
        {`${format(new Date(startDate), 'yyyy-MM-dd ~ ')}${format(
          new Date(endDate),
          'yyyy-MM-dd',
        )}`}
      </DateSelector>
      {isActiveCalendar && (
        <Container onClick={onCloseCalendar}>
          <CalendarModal>
            <DateRangePicker
              ranges={[
                {
                  startDate,
                  endDate,
                  key: 'selection',
                },
              ]}
              onChange={onChange}
              locale={ko}
              months={2}
              direction='horizontal'
              staticRanges={defaultStaticRanges}
              inputRanges={[]}
              showDateDisplay={false}
            />
            <ActionBtn
              title='취소'
              bgColor='white'
              color='gray'
              outlineColor='gray'
              onClick={onCancel}
              right='130px'
              bottom='16px'
            />
            <ActionBtn
              title='적용'
              onClick={onApply}
              right='20px'
              bottom='16px'
            />
          </CalendarModal>
        </Container>
      )}
    </>
  );
}
const Container = styled.div`
  position: absolute;
  background-color: transparent;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: 100;
`;
const Icon = styled.img`
  width: 24px;
  height: 24px;
`;
const DateSelector = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  gap: 6px;
  height: 36px;

  position: relative;
  padding: 4px 14px 4px 14px;
  border-radius: 4px;
  background-color: #ffffff;
  border: 1px solid #bdbdbd;
  color: #80848a;
  white-space: nowrap;
  font-size: 14px;
  letter-spacing: -0.28px;
`;

const CalendarModal = styled.div`
  position: absolute;
  z-index: 5px;
  top: 40px;
  right: 0;
  background-color: #ffffff;
  padding-bottom: 40px;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.12);
`;

export default memo(FXReceiptDateSelector);
